import { useCallback } from 'react';
import { EmbeddedMessage } from './types';

// Re-export types for convenience
export * from './types';

/**
 * Hook to post messages to the parent frame when embedded in an iframe.
 * Internally uses `useIsEmbedded` to verify the embedding is authorized
 * and messages are only sent to parent frames in the allowed origins list.
 *
 * @returns A function to post messages to the parent frame
 *
 * @example
 * ```tsx
 * const postMessageToParent = usePostMessageToParent();
 *
 * // Storefront: Post purchase complete message
 * postMessageToParent({
 *   type: EmbeddedStorefrontMessageType.PURCHASE_COMPLETE,
 *   orderId: 'ORD-123',
 *   timestamp: new Date().toISOString(),
 * });
 *
 * // Storefront: Post exit requested message
 * postMessageToParent({
 *   type: EmbeddedStorefrontMessageType.EXIT_REQUESTED,
 *   timestamp: new Date().toISOString(),
 * });
 *
 * // Ship Laptop: Post shipment complete message
 * postMessageToParent({
 *   type: EmbeddedShipLaptopMessageType.SHIPMENT_COMPLETE,
 *   shipmentId: 'SHIP-123',
 *   timestamp: new Date().toISOString(),
 * });
 *
 * // Ship Laptop: Post shipment error message
 * postMessageToParent({
 *   type: EmbeddedShipLaptopMessageType.SHIPMENT_ERROR,
 *   error: 'Error message',
 *   timestamp: new Date().toISOString(),
 * });
 * ```
 */
export const usePostMessageToParent = () => {
  const postMessage = useCallback((message: EmbeddedMessage) => {
    window.parent.postMessage(message, '*');
    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log(
        '[usePostMessageToParent] Posted message to parent:',
        message
      );
    }
  }, []);

  return postMessage;
};
